import 'Components/link-overlay/vm-link-overlay';
import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import placeholderSrc from 'Static/gfx/related/video-placeholder-short.svg';
import Styles from './vm-video-card-2025-12-1-short.scss';

/**
 * Short ("related") video-card layout — the web-component port of the
 * `templates/video/v2025_12_1/video_item_short.html` Django partial.
 *
 * Renders the `.related-item` markup: a left-hand thumbnail (with optional
 * duration overlay or placeholder) beside an info column holding the author,
 * the title, and a "Feat." contributor line with an optional view count.
 *
 * The component owns the visual layout, link wrapping, and the
 * placeholder/duration affordances; the host supplies the content via
 * attributes, with named slots to override the author and contributor markup.
 *
 * The whole card is clickable: a `vm-link-overlay` stretches a transparent
 * primary link over the entire `.related-item`, while the existing thumbnail,
 * title, publisher, and contributor anchors stay independently clickable
 * (elevated above the overlay). This adds no visual change — the thumbnail and
 * title keep their own anchors so their styling/hover behaviour is unchanged.
 *
 * @attr href           - Link destination for the thumbnail and title.
 * @attr heading        - Visible title text (rendered as h3).
 * @attr thumbnail-src  - Thumbnail image URL. If empty, a placeholder is shown.
 * @attr duration-label - Pre-formatted duration (e.g. "12:34") shown over the
 *                         thumbnail. Component does not format; caller decides.
 * @attr views-count    - Raw view count. Formatted compactly (e.g. 3420 → "3.4k")
 *                         and shown in the description row. Hidden when 0.
 * @attr publisher      - JSON `{name, href?}` object for the author/channel line.
 *                         Rendered as plain text or a link when `href` is present.
 * @attr contributors   - JSON array of `{name, href?}` items rendered after a
 *                         leading "Feat." label, comma-separated, each linked when
 *                         `href` is present.
 * @attr target         - Anchor `target` for the thumbnail and title links.
 * @slot publisher    - Override for the default author rendering.
 * @slot contributors - Override for the default "Feat." contributors rendering.
 */
@customElement('vm-video-card-2025-12-1-short')
export class VmVideoCard extends LitElement {
    static override styles = Styles;

    @property({ type: String }) href = '';
    @property({ type: String }) heading = '';
    @property({ type: String, attribute: 'thumbnail-src' }) thumbnailSrc = '';
    @property({ type: String, attribute: 'duration-label' }) durationLabel = '';
    @property({ type: Object }) publisher: { name: string; href?: string } | null = null;
    @property({ type: Array }) contributors: Array<{ name: string; href?: string }> = [];
    @property({ type: String }) target: HTMLAnchorElement['target'] = '';

    override connectedCallback() {
        super.connectedCallback();
        this.classList.add('vm-video-card');
        this.dataset.testid = 'vm-video-card';
    }

    private _renderDefaultPublisher() {
        if (!this.publisher?.name) return nothing;
        const { name, href } = this.publisher;
        return href ? html`<a href=${href}>${name}</a>` : html`${name}`;
    }

    private _renderDefaultContributors() {
        if (!this.contributors.length) return nothing;
        // Matches the template's "Feat. Name1, Name2" byline. Each contributor
        // is a link when an `href` is supplied, otherwise plain text.
        return html`
            Feat.
            ${this.contributors.map((contributor, i) => {
                const separator = i < this.contributors.length - 1 ? ', ' : '';
                return contributor.href
                    ? html`<a href=${contributor.href}>${contributor.name}</a>${separator}`
                    : html`<span>${contributor.name}${separator}</span>`;
            })}
        `;
    }

    private _renderThumbnail() {
        const src = this.thumbnailSrc || placeholderSrc;
        return html`<img src=${src} alt="" class="related-item__img" />`;
    }

    override render() {
        return html`
            <vm-link-overlay>
                <div class="related-item">
                    <div class="related-item__img-container">
                        <a
                            href=${this.href}
                            target=${this.target || nothing}
                            class="img-overlay related-item__img-link"
                            aria-label=${this.heading}
                        >
                            ${this._renderThumbnail()}
                            ${this.thumbnailSrc && this.durationLabel
                                ? html`<span class="overlay video-duration">${this.durationLabel}</span>`
                                : nothing}
                        </a>
                    </div>
                    <div class="related-item__info">
                        <div class="related-item__author">
                            <slot name="publisher">${this._renderDefaultPublisher()}</slot>
                        </div>
                        <h3 class="related-item__title">
                            <a href=${this.href} target=${this.target || nothing}>${this.heading}</a>
                        </h3>
                        <div class="related-item__desc">
                            <slot name="contributors">${this._renderDefaultContributors()}</slot>
                        </div>
                    </div>
                </div>
                <a
                    slot="link-overlay"
                    href=${this.href}
                    target=${this.target || nothing}
                    class="related-item__link"
                    aria-label=${this.heading}
                ></a>
            </vm-link-overlay>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-card-2025-12-1-short': VmVideoCard;
    }
}
